import { getAclViewClassName, getAgeInYears } from "@elixir/utils";
import { Drawer, Modal, notification } from "antd";
import React, { useEffect } from "react";
import { DrawerHeaderUi, PrimaryButton } from "ui";
import useGetDeveloperAuthInit from "../../../apis/parchi/use-get-developer-auth-init";
import useGetSaTemplate from "../../../apis/parchi/use-get-sa-template";
import usePostAppointmentSubmitSA from "../../../apis/tryst/use-post-appointment-submit-sa";
import { API_EKA_HOST, DEEPTHOUGHT_HOST } from "../../../constants/env";
import useQueueStore from "../../../stores/queue-store";
import { EVENT_TYPES } from "../../../utils/bridges";
import { dispatch } from "../../../utils/bridges/events/dispatch";

import { getIdb, onShowSAAction } from "../../../utils/bridges/methods";
import getB from "../../../utils/bridges/EkaBridge";
import { offlineAptToAppointment } from "../../../controllers/appointments/utils/converter-utilts";
import QuestionnaireList, {
  closeSaSubmissionFromList,
} from "../questionnaire-list";
import { track } from "../../../utils/tracking";
import { SUBJECT_KEYS } from "@elixir/types";

let closeSaApp: () => void;
let closeSaShowSubmissionApp: () => void;

declare global {
  interface Window {
    runSaApp: (config: {
      workflow_id: number;
      container_id: string;
      auth_token: string;
      refresh_token: string;
      params: {
        gender: "M" | "F" | "O";
        age: number;
      };
      practice_info?: {
        practitioner_uuid: string;
        patient_uuid: string;
        unique_identifier?: string;
      };
      onEndCallback?: (params: {
        success: boolean;
        data?: { assessment_id: string; workflow_id: number };
        error?: string;
      }) => void;
      onSaSubmitCallback?: (params: {
        data?: {
          assessment_id: string;
          workflow_id: number;
        };
        error?: string;
      }) => void;
      onSaRedirectCallback?: (params: {
        success: boolean;
        data?: {
          assessment_id: string;
          workflow_id: number;
          new_workflow_id: number;
        };
        error?: string;
      }) => void;
    }) => () => void;
    showSaSubmission: (config: {
      container_id: string;
      assessment_ids: Array<string>;
      auth_token: string;
      refresh_token: string;
      printConfig?: {
        header_html?: string;
        footer_html?: string;
        header_height?: string;
        footer_height?: string;
        head_html?: string;
      };
      onPrint?: (bodyHtml: string) => void;
    }) => () => void;
  }
}

const SaQuestionnaireDrawer = () => {
  useEffect(() => {
    // Check if the script is already added
    if (
      !document.querySelector(`script[src="${API_EKA_HOST}/sa-fe/v2/main.js"]`)
    ) {
      const script = document.createElement("script");
      script.src = `${API_EKA_HOST}/sa-fe/v2/main.js`;
      script.type = "module";
      document.body.appendChild(script);
    }

    // return () => {
    //   handleOnClose();
    // };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isQuestionnaireDrawerOpenedFromPad = useQueueStore(
    (state) => state.isQuestionnaireDrawerOpenedFromPad,
  );
  const setIsQuestionnaireDrawerOpenedFromPad = useQueueStore(
    (state) => state.setIsQuestionnaireDrawerOpenedFromPad,
  );

  const openQuestionnaireDrawer = useQueueStore(
    (state) => state.openQuestionnaireDrawer,
  );
  const setOpenQuestionnaireDrawer = useQueueStore(
    (state) => state.setOpenQuestionnaireDrawer,
  );

  const questionnaireWorkflowId = useQueueStore(
    (state) => state.questionnaireWorkflowId,
  );
  const setQuestionnaireWorkflowId = useQueueStore(
    (state) => state.setQuestionnaireWorkflowId,
  );
  const clearQuestionnaireWorkflowId = useQueueStore(
    (state) => state.clearQuestionnaireWorkflowId,
  );
  const workflowIdInShowResponse = useQueueStore(
    (state) => state.workflowIdInShowResponse,
  );
  const setWorkflowIdInShowResponse = useQueueStore(
    (state) => state.setWorkflowIdInShowResponse,
  );
  const showAssessmentIds = useQueueStore((state) => state.showAssessmentIds);
  const setShowAssessmentIds = useQueueStore(
    (state) => state.setShowAssessmentIds,
  );
  const clearShowResponseState = useQueueStore(
    (state) => state.clearShowResponseState,
  );

  const selectedAppointment = useQueueStore(
    (state) => state.selectedAppointment,
  );
  const setSelectedAppointment = useQueueStore(
    (state) => state.setSelectedAppointment,
  );

  const { data: developerAuthInitData } = useGetDeveloperAuthInit();
  const { mutateAsync: postAppointmentSubmitSa } = usePostAppointmentSubmitSA();

  const updateAptOnWorkflowCompletion = async ({
    aid,
    assessment_id,
    workflow_id,
  }: {
    aid: string;
    assessment_id: string;
    workflow_id: string;
  }) => {
    const appointment = await getIdb().appointments.get(aid);

    if (!appointment) {
      return;
    }

    const [patient] =
      (await getB().getPatientsByIds([appointment.patient_oid])) || [];

    const { success } = await postAppointmentSubmitSa({
      appointment: { aid },
      assessmentId: assessment_id,
      workflowId: workflow_id,
    });

    if (success) {
      const appointmentData = offlineAptToAppointment(
        {
          ...appointment,
          sa_cmpl: Array.from(
            new Set([...(appointment.sa_cmpl || []), workflow_id.toString()]),
          ),
        },
        patient,
      );
      setSelectedAppointment(appointmentData);
    } else {
      track("ERROR", {
        on: "submit_assessment",
        what: "failed",
        aid: appointment.aid,
      });
      notification.error({
        message: "Error",
        description: "Failed to submit assessment",
      });
    }
  };

  const handleExitQuestionnaire = () => {
    closeSaApp?.();
    clearQuestionnaireWorkflowId();
  };

  const handleCloseDrawer = () => {
    setOpenQuestionnaireDrawer(false);
    setSelectedAppointment();
    if (isQuestionnaireDrawerOpenedFromPad) {
      setIsQuestionnaireDrawerOpenedFromPad(false);
      dispatch({
        type: EVENT_TYPES.CLOSE_QUESTIONNAIRE_LIST,
      });
    }
  };

  const handleOnFillNowClick = (workflowId: number) => {
    if (workflowId) {
      if (
        developerAuthInitData?.data?.access_token &&
        developerAuthInitData.data.refresh_token
      ) {
        closeSaApp = window.runSaApp({
          workflow_id: workflowId,
          container_id: "sa-container",
          auth_token: developerAuthInitData.data.access_token,
          refresh_token: developerAuthInitData.data.refresh_token,
          params: {
            gender: selectedAppointment?.patient.gen as "M" | "F" | "O",
            age: selectedAppointment?.patient.dob
              ? getAgeInYears(new Date(selectedAppointment?.patient.dob))
              : 0,
          },
          practice_info: {
            practitioner_uuid: selectedAppointment?.doctor_oid as string,
            patient_uuid: selectedAppointment?.patient_uuid as string,
            unique_identifier: selectedAppointment?.patient_oid,
          },
          onSaSubmitCallback: ({ data, error }) => {
            console.log("runSaApp:onSaSubmitCallback", data, error);
            if (data && selectedAppointment) {
              const { assessment_id, workflow_id } = data;
              updateAptOnWorkflowCompletion({
                aid: selectedAppointment.aid,
                assessment_id,
                workflow_id: workflow_id.toString(),
              });
              notification.success({
                message: "Success",
                description: "Assessment submitted successfully",
              });
            }
            if (error) {
              notification.error({
                message: "Error",
                description: error,
              });
            }
          },
          onEndCallback: ({ success, data, error }) => {
            console.log("runSaApp:onEndCallback", success, data, error);
            handleExitQuestionnaire();
          },
          onSaRedirectCallback: ({ success, data, error }) => {
            console.log("runSaApp:onSaRedirectCallback", success, data, error);
            if (success && data && selectedAppointment) {
              const { assessment_id, workflow_id, new_workflow_id } = data;
              updateAptOnWorkflowCompletion({
                aid: selectedAppointment.aid,
                assessment_id,
                workflow_id: workflow_id.toString(),
              });
              if (new_workflow_id) {
                setQuestionnaireWorkflowId(new_workflow_id);
              }
            }
            if (error) {
              notification.error({
                message: "Error",
                description: error,
              });
            }
          },
        });
      }
    }
  };

  const { data } = useGetSaTemplate({
    selectedDoctorOid: selectedAppointment?.doctor_oid,
    enabled: !!openQuestionnaireDrawer,
  });
  const assessmentsList = data?.template;

  const selectedWorflowName = React.useMemo(() => {
    if (workflowIdInShowResponse) {
      return (
        assessmentsList?.find(
          (workflow) =>
            workflow.meta.sa_id === workflowIdInShowResponse.toString(),
        )?.meta.display.title || ""
      );
    }
    if (!questionnaireWorkflowId) {
      return "";
    }
    return (
      assessmentsList?.find(
        (workflow) =>
          workflow.meta.sa_id === questionnaireWorkflowId.toString(),
      )?.meta.display.title || ""
    );
  }, [assessmentsList, questionnaireWorkflowId, workflowIdInShowResponse]);

  const [showExitPopup, setShowExitPopup] = React.useState<boolean>(false);

  const handleEkaAIClick = () => {
    if (selectedAppointment && selectedAppointment.assessments) {
      const uniqueIdentifier = selectedAppointment.patient_oid;
      const patientUuid = selectedAppointment.patient_uuid;
      const practitionerUuid = selectedAppointment.doctor_oid;
      const patientName = selectedAppointment.patient.fln;
      const url = `${DEEPTHOUGHT_HOST}/?unique_identifier=${uniqueIdentifier}&patient_uuid=${patientUuid}&practitioner_uuid=${practitionerUuid}&patient_name=${encodeURIComponent(patientName)}`;

      window.open(url, "_blank");
    }
  };

  const handleViewAllResponsesClick = async () => {
    if (selectedAppointment && selectedAppointment.sa_cmpl?.length) {
      const sa_cmpl = selectedAppointment.sa_cmpl;
      const getSaIdsPromises = sa_cmpl.map((workflowId) =>
        getB().appointments.getAssementIdWorkflow(
          selectedAppointment.aid,
          workflowId,
        ),
      );

      const saIdsResults = await Promise.all(getSaIdsPromises);
      const sa_ids = saIdsResults
        .flatMap((result) => result?.sa_id || [])
        .filter((id) => id);

      if (selectedAppointment.sa_cmpl?.length === 1) {
        setWorkflowIdInShowResponse(parseInt(selectedAppointment.sa_cmpl[0]));
      }
      setShowAssessmentIds(sa_ids);
      if (
        window.showSaSubmission &&
        developerAuthInitData?.data?.access_token &&
        developerAuthInitData.data.refresh_token
      ) {
        closeSaShowSubmissionApp = window.showSaSubmission({
          assessment_ids: sa_ids,
          container_id: "sa-container",
          auth_token: developerAuthInitData.data.access_token,
          refresh_token: developerAuthInitData.data.refresh_token,
        });
        return;
      }
      onShowSAAction({
        sa_ids,
      });
      handleCloseDrawer();
    } else {
      notification.info({
        message: "No Assessments",
        description:
          "There are no completed assessments to show responses for.",
      });
    }
  };

  return (
    <>
      <Modal
        zIndex={1000}
        title="Are you sure you want to exit?"
        open={showExitPopup}
        onOk={() => {
          setShowExitPopup(false);
          handleExitQuestionnaire();
        }}
        onCancel={() => setShowExitPopup(false)}
      >
        <p>
          Your progress will be saved for the next 24 hours. You can resume
          anytime within that period.
        </p>
      </Modal>
      <Drawer
        style={{ padding: 0 }}
        bodyStyle={{ padding: 0 }}
        footer={null}
        closable={false}
        placement={"right"}
        onClose={() => {
          if (questionnaireWorkflowId) {
            setShowExitPopup(true);
            return;
          }
          if (showAssessmentIds.length > 0) {
            clearShowResponseState();
            closeSaShowSubmissionApp?.();
            closeSaSubmissionFromList?.();
            return;
          }
          handleCloseDrawer();
        }}
        open={!!openQuestionnaireDrawer}
        destroyOnClose={true}
        maskClosable={false}
        width={"60%"}
        zIndex={500}
      >
        <div className="dr-w-full dr-h-full dr-flex dr-flex-col">
          <DrawerHeaderUi
            title={selectedWorflowName || "Questionnaires"}
            onClose={() => {
              if (questionnaireWorkflowId) {
                setShowExitPopup(true);
                return;
              }
              if (showAssessmentIds.length > 0) {
                clearShowResponseState();
                closeSaShowSubmissionApp?.();
                closeSaSubmissionFromList?.();
                return;
              }
              handleCloseDrawer();
            }}
            headerRightComponent={
              selectedWorflowName || showAssessmentIds.length > 0 ? null : (
                <div className="dr-space-x-8 dr-flex dr-justify-center dr-items-center">
                  {selectedAppointment?.assessments && (
                    <>
                      <PrimaryButton
                        title="EkaAI Assessments (BETA)"
                        onClick={handleEkaAIClick}
                        extraClass={getAclViewClassName(SUBJECT_KEYS.EKA_AI_ASSESSMENTS)}
                      />
                      <PrimaryButton
                        title="View All Responses"
                        onClick={handleViewAllResponsesClick}
                      />
                    </>
                  )}
                </div>
              )
            }
          />
          {!questionnaireWorkflowId && showAssessmentIds.length === 0 ? (
            <QuestionnaireList onFillNowClick={handleOnFillNowClick} />
          ) : null}
          <div
            id="sa-container"
            className={`dr-w-full dr-flex-1 dr-overflow-hidden ${
              questionnaireWorkflowId || showAssessmentIds.length > 0 ? "" : "dr-hidden"
            }`}
          />
        </div>
      </Drawer>
    </>
  );
};

export default SaQuestionnaireDrawer;
